import { Loader, Modal, NumberInput } from '@mantine/core';
import { IconArrowRight, IconInfoCircle } from '@tabler/icons-react';
import type { VolumeResponse } from '@xver/apis/v1/kufer/volumes/model';
import { Button } from '@xver/components/Atoms';
import { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePricingQuery } from '@xver/hooks/usePricingQuery';

import { useVolumeMutations } from '../hooks/useVolumeMutations';
import { formatBaht, resolveVolumeRate, volumeMonthlyCost } from '../utils/volumeCost';
import {
    MAX_VOLUME_GIB,
    validateResize,
    VOLUME_ERROR_DEFAULTS,
    type VolumeErrorKey,
} from '../utils/volumeValidation';

interface ResizeVolumeModalProps {
    containerId: string;
    volume: VolumeResponse | null;
    opened: boolean;
    onClose: () => void;
}

const NUMBER_INPUT_STYLES = {
    input: {
        backgroundColor: 'var(--surface-2)',
        borderColor: 'var(--border)',
        color: 'var(--fg)',
    },
};

export default function ResizeVolumeModal({
    containerId,
    volume,
    opened,
    onClose,
}: ResizeVolumeModalProps) {
    const { t } = useTranslation('containerEngineInstance');
    const { resizeVolume } = useVolumeMutations(containerId);
    const { data: pricing } = usePricingQuery();
    const volumeRate = resolveVolumeRate(pricing?.products.container.rates.volume_gb_monthly_rate);

    const currentSize = volume?.size_gib ?? 0;
    const [sizeGiB, setSizeGiB] = useState<number>(currentSize + 1);

    useEffect(() => {
        if (!opened || !volume) return;
        setSizeGiB(Math.min(volume.size_gib + 1, MAX_VOLUME_GIB));
    }, [opened, volume]);

    const message = (key: VolumeErrorKey | null) =>
        key ? t(`volumes.validation.${key}`, VOLUME_ERROR_DEFAULTS[key]) : undefined;

    const sizeError = volume ? validateResize(sizeGiB, currentSize) : null;
    const isSubmitting = resizeVolume.isPending;
    const isValid = !!volume && !sizeError;

    const handleSubmit = (event?: FormEvent) => {
        event?.preventDefault();
        if (!volume || !isValid || isSubmitting) return;
        resizeVolume.mutate({ volumeId: volume.id, sizeGiB }, { onSuccess: onClose });
    };

    return (
        <Modal
            size="md"
            opened={opened}
            onClose={isSubmitting ? () => {} : onClose}
            withCloseButton={false}
            overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
            transitionProps={{ transition: 'fade', duration: 128, timingFunction: 'linear' }}
            centered
        >
            <form onSubmit={handleSubmit} className="flex flex-col justify-start items-start gap-4">
                <div className="flex flex-col justify-start items-start">
                    <Modal.Title>
                        <span className="text-base font-bold">
                            {t('volumes.resizeTitle', 'Resize volume')}
                        </span>
                    </Modal.Title>
                    <p className="text-sm text-fg/68">
                        {t('volumes.resizeDescription', {
                            name: volume?.name ?? '',
                            defaultValue: 'Add more space to "{{name}}".',
                        })}
                    </p>
                </div>

                <div className="w-full flex flex-col justify-start items-start gap-4">
                    <div className="w-full flex flex-row items-center justify-center gap-4 p-3 rounded-md bg-fg/5">
                        <div className="flex flex-col items-center">
                            <span className="text-xs text-fg/68">
                                {t('volumes.resizeCurrent', 'Current size')}
                            </span>
                            <span className="text-sm font-mono text-fg">
                                {t('volumes.sizeValue', {
                                    size: currentSize,
                                    defaultValue: '{{size}} GiB',
                                })}
                            </span>
                            <span className="text-xs font-mono text-fg/68">
                                {t('volumes.costValue', {
                                    amount: formatBaht(volumeMonthlyCost(currentSize, volumeRate)),
                                    defaultValue: '{{amount}} ฿',
                                })}
                            </span>
                        </div>
                        <IconArrowRight size={18} className="text-fg/40" aria-hidden="true" />
                        <div className="flex flex-col items-center">
                            <span className="text-xs text-fg/68">
                                {t('volumes.resizeNew', 'New size')}
                            </span>
                            <span className="text-sm font-mono text-fg">
                                {t('volumes.sizeValue', {
                                    size: sizeGiB,
                                    defaultValue: '{{size}} GiB',
                                })}
                            </span>
                            <span className="text-xs font-mono text-fg/68">
                                {t('volumes.costValue', {
                                    amount: formatBaht(volumeMonthlyCost(sizeGiB, volumeRate)),
                                    defaultValue: '{{amount}} ฿',
                                })}
                            </span>
                        </div>
                    </div>

                    <NumberInput
                        size="md"
                        label={t('volumes.sizeLabel', 'Size (GiB)')}
                        min={currentSize + 1}
                        max={MAX_VOLUME_GIB}
                        clampBehavior="none"
                        allowDecimal={false}
                        allowNegative={false}
                        value={sizeGiB}
                        onChange={(value) => setSizeGiB(Number(value))}
                        error={message(sizeError)}
                        disabled={isSubmitting}
                        styles={NUMBER_INPUT_STYLES}
                        autoFocus
                    />

                    <div className="w-full flex flex-col gap-2">
                        <div className="flex flex-row items-start gap-2">
                            <IconInfoCircle size={16} className="shrink-0 mt-0.5 text-fg/50" />
                            <p className="text-xs text-fg/68">
                                {t(
                                    'volumes.resizeGrowOnlyNote',
                                    'Volumes can only grow. To use less space, delete the volume and create a smaller one.'
                                )}
                            </p>
                        </div>
                        <div className="flex flex-row items-start gap-2">
                            <IconInfoCircle size={16} className="shrink-0 mt-0.5 text-fg/50" />
                            <p className="text-xs text-fg/68">
                                {t(
                                    'volumes.resizeNoRedeployNote',
                                    'Resizing happens live — the container keeps running and is not redeployed.'
                                )}
                            </p>
                        </div>
                        <div className="flex flex-row items-start gap-2">
                            <IconInfoCircle size={16} className="shrink-0 mt-0.5 text-fg/50" />
                            <p className="text-xs text-fg/68">
                                {t(
                                    'volumes.resizeCostNote',
                                    'The new monthly cost applies from the moment the volume is resized.'
                                )}
                            </p>
                        </div>
                    </div>

                    <div className="w-full flex flex-col justify-start items-start gap-2.5">
                        <Button width="100%" type="submit" disabled={!isValid || isSubmitting}>
                            {isSubmitting ? (
                                <div className="flex items-center gap-2">
                                    <Loader size={16} color="white" />
                                    <span>{t('volumes.resizeSubmitting', 'Resizing…')}</span>
                                </div>
                            ) : (
                                t('volumes.resizeSubmit', 'Resize volume')
                            )}
                        </Button>
                        <Button
                            width="100%"
                            variant="secondary"
                            onClick={onClose}
                            disabled={isSubmitting}
                        >
                            {t('volumes.cancel', 'Cancel')}
                        </Button>
                    </div>
                </div>
            </form>
        </Modal>
    );
}
